import { useState } from 'react'
import { Landmark, PlugZap, Send } from 'lucide-react'
import { populateBanks, sendWebhook, testQiTech } from '../../services/api'
import Loader from '../../components/Loader'
import { Alert, Button, Card, JsonViewer, PageHeader } from '../../components/ui'

// Payload de exemplo retirado da collection (BackOffice > Utils > Webhook)
const WEBHOOK_TEMPLATE = JSON.stringify(
	{
		key: '76c248ce-4d7e-4437-8fe4-986611336368',
		data: {
			amount: 1,
			origin: {
				name: 'Default',
				branch: '0001',
				document: '46408822000107',
				account_key: 'a3abccc4-4ce0-4003-9b03-844197486474',
				account_digit: '0',
				account_number: '9198628',
			},
			timestamp: '2026-07-13T19:00:59.000000',
			description: '329 0001 3316467-3 64.023.089/0001-94 - KSI SOLUCOES FINANCEIRAS LTDA',
			destination: {
				name: 'Default',
				branch: '0001',
				document: '64023089000194',
				account_key: '76c248ce-4d7e-4437-8fe4-986611336368',
				account_digit: '3',
				account_number: '3316467',
			},
			reference_key: '1d360882-fa8e-43e2-b2c0-203d1ba11bb4',
			reference_type: 'ted_outgoing',
			account_balance: 4.37,
			source_sub_type: 'internal_funds_transfer',
			transaction_key: '14ba3530-0538-4d0d-a61b-cef77f2905df',
			source_sub_type_str: 'Transferencia Interna',
		},
		datetime: '2026-07-13T19:00:59.000000',
		webhook_type: 'account_transaction',
	},
	null,
	2
)

const Utils = () => {
	const [qiTech, setQiTech] = useState({ loading: false, result: null, error: '' })
	const [banks, setBanks] = useState({ loading: false, done: false, error: '' })
	const [webhook, setWebhook] = useState({
		body: WEBHOOK_TEMPLATE,
		loading: false,
		result: null,
		error: '',
	})

	const handleTestQiTech = async () => {
		setQiTech({ loading: true, result: null, error: '' })
		try {
			const result = await testQiTech()
			setQiTech({ loading: false, result: result ?? { status: 'ok' }, error: '' })
		} catch (err) {
			setQiTech({ loading: false, result: null, error: err.message })
		}
	}

	const handlePopulateBanks = async () => {
		setBanks({ loading: true, done: false, error: '' })
		try {
			await populateBanks()
			setBanks({ loading: false, done: true, error: '' })
		} catch (err) {
			setBanks({ loading: false, done: false, error: err.message })
		}
	}

	const handleSendWebhook = async (e) => {
		e.preventDefault()

		let payload
		try {
			payload = JSON.parse(webhook.body)
		} catch (err) {
			setWebhook((prev) => ({ ...prev, error: `JSON invalido: ${err.message}`, result: null }))
			return
		}

		setWebhook((prev) => ({ ...prev, loading: true, error: '', result: null }))
		try {
			const result = await sendWebhook(payload)
			setWebhook((prev) => ({
				...prev,
				loading: false,
				result: result ?? { status: 'enviado' },
			}))
		} catch (err) {
			setWebhook((prev) => ({ ...prev, loading: false, error: err.message }))
		}
	}

	return (
		<div>
			<PageHeader
				title="Utils"
				subtitle="Ferramentas administrativas da pasta BackOffice > Utils"
			/>

			<div className="space-y-6">
				<Card
					title="Teste de autenticacao QI Tech"
					description="POST /backoffice/qitech/test"
					actions={
						<Button onClick={handleTestQiTech} disabled={qiTech.loading}>
							{qiTech.loading ? <Loader size="sm" /> : <PlugZap className="h-4 w-4" />}
							Executar teste
						</Button>
					}
				>
					<Alert onDismiss={() => setQiTech((prev) => ({ ...prev, error: '' }))}>
						{qiTech.error}
					</Alert>
					{qiTech.result ? (
						<JsonViewer data={qiTech.result} />
					) : (
						<p className="text-sm text-gray-500">
							Valida as credenciais de integracao com a QI Tech e retorna o resultado.
						</p>
					)}
				</Card>

				<Card
					title="Popular lista de bancos"
					description="POST /backoffice/banks/populate"
					actions={
						<Button onClick={handlePopulateBanks} disabled={banks.loading}>
							{banks.loading ? <Loader size="sm" /> : <Landmark className="h-4 w-4" />}
							Popular bancos
						</Button>
					}
				>
					<Alert onDismiss={() => setBanks((prev) => ({ ...prev, error: '' }))}>
						{banks.error}
					</Alert>
					<Alert type="success" onDismiss={() => setBanks((prev) => ({ ...prev, done: false }))}>
						{banks.done ? 'Lista de bancos populada com sucesso' : ''}
					</Alert>
					<p className="text-sm text-gray-500">
						Recarrega a tabela de bancos a partir da fonte oficial.
					</p>
				</Card>

				<Card title="Webhook" description="POST /webhooks">
					<form onSubmit={handleSendWebhook} className="space-y-4">
						<Alert onDismiss={() => setWebhook((prev) => ({ ...prev, error: '' }))}>
							{webhook.error}
						</Alert>

						<div>
							<label
								htmlFor="webhookBody"
								className="mb-1 block text-sm font-medium text-gray-700"
							>
								Payload JSON
							</label>
							<textarea
								id="webhookBody"
								rows={18}
								spellCheck={false}
								className="block w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
								value={webhook.body}
								onChange={(e) => setWebhook((prev) => ({ ...prev, body: e.target.value }))}
							/>
						</div>

						<div className="flex items-center gap-2">
							<Button type="submit" disabled={webhook.loading}>
								{webhook.loading ? <Loader size="sm" /> : <Send className="h-4 w-4" />}
								Enviar webhook
							</Button>
							<Button
								type="button"
								variant="secondary"
								onClick={() =>
									setWebhook({ body: WEBHOOK_TEMPLATE, loading: false, result: null, error: '' })
								}
							>
								Restaurar exemplo
							</Button>
						</div>

						{webhook.result && <JsonViewer data={webhook.result} />}
					</form>
				</Card>
			</div>
		</div>
	)
}

export default Utils
